import { Button } from '@shared/components/ui/button';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { usePayerBankNameById } from '@modules/Wallets/BankAccounts/hooks/usePayerBanks';
import { resolvePayerBankName } from '@modules/Wallets/BankAccounts/lib/resolvePayerBankName';
import { faDownload, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WithdrawalPaymentFile } from '../../models';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export interface PaymentFileCardProps extends RenderItemProps<WithdrawalPaymentFile> {
  className?: string;
  showActions?: boolean;
  onViewDetail?: (file: WithdrawalPaymentFile) => void;
  onDownload?: (file: WithdrawalPaymentFile) => void;
  downloadingFileId?: number | null;
}

/** Tarjeta de fichero de pago para vista móvil - Payment file card for mobile view */
export const PaymentFileCard = React.memo(function PaymentFileCard({
  item: paymentFile,
  className = '',
  showActions = true,
  onViewDetail,
  onDownload,
  downloadingFileId,
}: PaymentFileCardProps) {
  const { t } = useTranslation('withdrawalPaymentFilesModule');
  const { nameById } = usePayerBankNameById();
  const isDownloading = downloadingFileId === paymentFile.id;

  return (
    <div
      className={`py-4 border-b border-card-border hover:bg-hover-press/30 transition-colors ${className}`}
      data-testid={`payment-file-card-${paymentFile.id}`}
    >
      <div className="space-y-3">
        {/* Cabecera: ID fichero - File ID header */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-title">#{paymentFile.id}</span>
          <span className="text-xs text-subtext shrink-0">{formatDate(paymentFile.created_at)}</span>
        </div>

        {/* ID generación - Generation ID */}
        <div className="min-w-0">
          <p className="text-xs text-subtext mb-0.5">{t('table.columns.generation_id')}</p>
          <p className="font-mono text-xs text-title break-all leading-relaxed">{paymentFile.generation_id}</p>
        </div>

        {/* Banco pagador y nº retiradas - Payer bank and withdrawals count */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="min-w-0">
            <p className="text-xs text-subtext mb-0.5">{t('table.columns.payer_bank')}</p>
            <p className="font-medium text-title truncate">
              {resolvePayerBankName(paymentFile.payer_bank_id, paymentFile.payer_bank, nameById)}
            </p>
          </div>
          <div>
            <p className="text-xs text-subtext mb-0.5">{t('table.columns.withdrawals_count')}</p>
            <p className="font-medium text-title">
              {paymentFile.withdrawals_count ?? paymentFile.withdrawal_count ?? '—'}
            </p>
          </div>
        </div>

        {/* Acciones - Actions */}
        {showActions && (
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => onViewDetail?.(paymentFile)}
              data-testid={`payment-file-card-view-${paymentFile.id}`}
            >
              <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5 mr-1.5" />
              {t('actions.view')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => onDownload?.(paymentFile)}
              disabled={isDownloading}
              data-testid={`payment-file-card-download-${paymentFile.id}`}
            >
              <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5 mr-1.5" />
              {isDownloading ? t('actions.downloading') : t('actions.download')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});
